import { useQueries } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import type {
  ReportingArtifact,
  ReportingChatMessage,
} from 'src/agent/reporting/reportingPresentation.types'
import type {
  DraftDisplayProjection,
  ReportingRunProjection,
} from 'src/queries/reportingAgent/reportingAgent.models'
import { reportingAgentQueries } from 'src/queries/reportingAgent/reportingAgent.queries'

import { reportingErrorStatus } from './reportingConversation.errors'

export interface UseReportingConversationArtifactsOptions {
  actorId?: string
  organizationId?: string
  conversationId?: string
  messages?: readonly ReportingChatMessage[]
  activeRunId?: string
  activeArtifact?: ReportingArtifact
  reportingEnabled?: boolean
  onAccessDenied?: (status: number) => void
}

export interface ReportingConversationArtifactsResult {
  readonly artifactByRunId: ReadonlyMap<string, ReportingArtifact>
}

interface CommittedRun {
  readonly id: string
  readonly artifactId: string
}

const toPresentationArtifact = (
  value: DraftDisplayProjection,
): ReportingArtifact => ({
  id: value.artifact_id,
  title: value.title,
  liveReportCandidateId: value.live_report_candidate_id,
  chartSpec: value.chart_spec,
  dataset: value.dataset,
})

const isCommittedRun = (
  value: ReportingRunProjection | undefined,
  conversationId: string | undefined,
): value is ReportingRunProjection & { artifact_id: string } =>
  Boolean(
    value &&
      conversationId &&
      value.conversation_id === conversationId &&
      value.status === 'completed' &&
      value.artifact_id,
  )

export const useReportingConversationArtifacts = ({
  actorId,
  organizationId,
  conversationId,
  messages = [],
  activeRunId,
  activeArtifact,
  reportingEnabled = true,
  onAccessDenied,
}: UseReportingConversationArtifactsOptions): ReportingConversationArtifactsResult => {
  const runIds = useMemo(() => {
    const ids = new Set<string>()
    for (const message of messages) {
      if (message.runId !== null && message.runId !== activeRunId)
        ids.add(message.runId)
    }
    return [...ids]
  }, [activeRunId, messages])

  const runQueries = useQueries({
    queries: runIds.map((runId) =>
      reportingAgentQueries.run({
        actorId,
        organizationId,
        runId,
        reportingEnabled,
      }),
    ),
  })

  const committedRuns = useMemo<readonly CommittedRun[]>(() => {
    return runQueries.reduce<CommittedRun[]>((runs, result, index) => {
      const run = result.data as ReportingRunProjection | undefined
      if (isCommittedRun(run, conversationId)) {
        runs.push({ id: run.id, artifactId: run.artifact_id })
      }
      return runs
    }, [])
  }, [conversationId, runQueries])

  const displayQueries = useQueries({
    queries: committedRuns.map(({ artifactId }) =>
      reportingAgentQueries.draftDisplay({
        actorId,
        organizationId,
        conversationId,
        artifactId,
        reportingEnabled,
      }),
    ),
  })

  const deniedQueryError = [...runQueries, ...displayQueries]
    .filter((result) => result.isError)
    .map((result) => result.error)
    .find((error) => {
      const status = reportingErrorStatus(error)
      return status === 401 || status === 403
    })

  useEffect(() => {
    if (deniedQueryError) {
      onAccessDenied?.(reportingErrorStatus(deniedQueryError) ?? 403)
    }
  }, [deniedQueryError, onAccessDenied])

  const artifactByRunId = useMemo(() => {
    const artifacts = new Map<string, ReportingArtifact>()
    committedRuns.forEach((run, index) => {
      const display = displayQueries[index]?.data as
        | DraftDisplayProjection
        | undefined
      if (
        display &&
        display.conversation_id === conversationId &&
        display.artifact_id === run.artifactId
      ) {
        artifacts.set(run.id, toPresentationArtifact(display))
      }
    })
    if (activeRunId && activeArtifact) {
      artifacts.set(activeRunId, activeArtifact)
    }
    return artifacts
  }, [
    activeArtifact,
    activeRunId,
    committedRuns,
    conversationId,
    displayQueries,
  ])

  return { artifactByRunId }
}

export default useReportingConversationArtifacts
